import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Divider,
  TextField,
  Button,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  InputAdornment,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import TopBar from '../components/TopBar';
import TroopZonePickerMap from '../components/TroopZonePickerMap';
import { getImageUrl } from '../components/UserIdentity';
import { RandomAvatar } from 'react-random-avatar';
import imageCompression from 'browser-image-compression';
import '../App.css';
import { getApiUrl } from '../apiUrl';

const Account = () => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [photoURL, setPhotoURL] = useState('');
  const [avatarSeed, setAvatarSeed] = useState('');
  const [open, setOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [error, setError] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  const [troopDialogOpen, setTroopDialogOpen] = useState(false);
  const [troopStep, setTroopStep] = useState(1); // 1 zip, 2 picker, 3 confirm
  const [newZipCode, setNewZipCode] = useState('');
  const [zipValid, setZipValid] = useState(false);
  const [zipValidating, setZipValidating] = useState(false);
  const [userCenter, setUserCenter] = useState(null);
  const [zones, setZones] = useState([]);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [updatingTroop, setUpdatingTroop] = useState(false);

  const API_URL = getApiUrl();

  const refreshSession = async () => {
    const response = await axios.get(`${API_URL}/check-session`);
    if (response.data.isLoggedIn) {
      setUser(response.data);
      setPhotoURL(response.data.profilePhoto || '');
      setAvatarSeed(response.data.avatarSeed || '');
      return response.data;
    }
    navigate('/dashboard');
    return null;
  };

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        await refreshSession();
        setAuthChecked(true);
      } catch (err) {
        console.error('Error checking session:', err);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const handleClickOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setConfirmationText('');
    setError('');
  };

  const handleDelete = async () => {
    if (confirmationText !== 'Close account') {
      setError('Please type "Close account" to confirm deletion.');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/delete-account`, { confirmationText });
      if (response.status === 200) navigate('/');
    } catch (_err) {
      setError('Failed to delete account. Please try again.');
    }
  };

  const uploadToR2 = async (file) => {
    const options = {
      maxSizeMB: 2,
      maxWidthOrHeight: 2000,
      useWebWorker: true
    };

    const compressedFile = await imageCompression(file, options);
    const formData = new FormData();
    formData.append('image', compressedFile, file.name);

    // Do not set Content-Type manually — browser must add multipart boundary.
    // Authorization Bearer is attached by the App.js axios interceptor.
    const response = await axios.post(`${API_URL}/api/upload-image`, formData);

    if (response.data.success) {
      return response.data.photo.url;
    }
    throw new Error('Upload failed');
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!user || !user._id) {
      alert('User ID is not available. Please try again.');
      return;
    }

    setUploadingPhoto(true);
    try {
      const uploadedUrl = await uploadToR2(file);

      const response = await axios.post(`${API_URL}/update-profile`, {
        profilePhoto: uploadedUrl,
        avatarSeed: null
      });

      if (response.status === 200) {
        setUser({ ...user, profilePhoto: uploadedUrl, avatarSeed: null });
        setPhotoURL(uploadedUrl);
        setAvatarSeed('');
      }
    } catch (err) {
      console.error('Photo upload failed:', err);
      alert('Error uploading photo: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const generateAvatarSeed = () => Math.random().toString(36).substring(2, 15);

  const handleGenerateAvatar = async () => {
    const newSeed = generateAvatarSeed();
    setAvatarSeed(newSeed);

    try {
      const response = await axios.post(`${API_URL}/update-profile`, {
        avatarSeed: newSeed,
        profilePhoto: null
      });

      if (response.status === 200) {
        setUser({ ...user, avatarSeed: newSeed, profilePhoto: null });
        setPhotoURL('');
      }
    } catch (err) {
      console.error('Avatar generation failed:', err);
      alert('Error generating avatar: ' + (err.response?.data?.error || err.message));
    }
  };

  const validateZipCode = async (zip) => {
    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!zipRegex.test(zip)) {
      setZipValid(false);
      return false;
    }

    setZipValidating(true);
    try {
      const response = await axios.get(`${API_URL}/api/geocode/zip`, {
        params: { zip },
        timeout: 15000
      });

      if (response.data?.success) {
        setUserCenter(response.data.coordinates);
        setZipValid(true);
        setZipValidating(false);
        return true;
      }
      setZipValid(false);
      setZipValidating(false);
      return false;
    } catch (err) {
      console.error('Zip code validation error:', err);
      setZipValid(false);
      setZipValidating(false);
      return false;
    }
  };

  useEffect(() => {
    if (troopDialogOpen && troopStep === 1 && newZipCode.length === 5) {
      validateZipCode(newZipCode);
    } else if (troopStep === 1) {
      setZipValid(false);
    }
  }, [newZipCode, troopDialogOpen, troopStep]);

  const openTroopChange = () => {
    setNewZipCode(user?.zipCode || '');
    setTroopStep(1);
    setSelectedZoneId(null);
    setZones([]);
    setUserCenter(null);
    setTroopDialogOpen(true);
  };

  const handleZipNext = async () => {
    if (!zipValid || !userCenter) return;
    try {
      const nearby = await axios.get(`${API_URL}/api/troop-zones/nearby`, {
        params: { lat: userCenter[1], lng: userCenter[0], limit: 20 }
      });
      setZones(nearby.data || []);
      if (nearby.data?.[0]) setSelectedZoneId(nearby.data[0].id);
      setTroopStep(2);
    } catch (err) {
      alert('Failed to load troops: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleConfirmTroop = async () => {
    if (!selectedZoneId) return;
    setUpdatingTroop(true);
    try {
      const response = await axios.post(`${API_URL}/api/profile/change-troop`, {
        troopZoneId: selectedZoneId,
        zipCode: newZipCode,
        confirmReset: true
      });
      if (response.data?.user) {
        setUser({ ...user, ...response.data.user, isLoggedIn: true });
      } else {
        await refreshSession();
      }
      setConfirmResetOpen(false);
      setTroopDialogOpen(false);
      setTroopStep(1);
      alert('Troop updated. Your troop reputation was reset.');
    } catch (err) {
      alert('Error changing troop: ' + (err.response?.data?.error || err.message));
    } finally {
      setUpdatingTroop(false);
    }
  };

  const handleBack = () => navigate('/dashboard');

  if (loading || !authChecked) {
    return <div>Loading...</div>;
  }

  const troopName = user?.troopZone?.name || 'Not joined';

  return (
    <Box sx={{
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: 'url(/bg1.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        filter: 'blur(8px) brightness(0.4)',
        zIndex: 0,
      }
    }}>
      <TopBar username={user?.username} />
      <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Card sx={{
          mt: 8,
          p: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.3)'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <IconButton size="small" onClick={handleBack} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography component="h1" variant="h5">
                Account
              </Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Profile Photo
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                {user?.profilePhoto || photoURL ? (
                  <Avatar
                    src={getImageUrl(user?.profilePhoto || photoURL)}
                    sx={{ width: 100, height: 100 }}
                  />
                ) : user?.avatarSeed || avatarSeed ? (
                  <Avatar sx={{ width: 100, height: 100 }}>
                    <RandomAvatar seed={user?.avatarSeed || avatarSeed} size={11} />
                  </Avatar>
                ) : (
                  <Avatar sx={{ width: 100, height: 100 }}>
                    {user?.username?.charAt(0).toUpperCase()}
                  </Avatar>
                )}
              </Box>
              <Button variant="outlined" onClick={handleGenerateAvatar} sx={{ mb: 2 }}>
                Generate Random Avatar
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
                id="photo-upload-input"
              />
              <label htmlFor="photo-upload-input">
                <Button
                  variant="contained"
                  component="span"
                  fullWidth
                  startIcon={uploadingPhoto ? <CircularProgress size={20} /> : <CameraAltIcon />}
                  disabled={uploadingPhoto}
                  sx={{ mt: 1, mb: 2, backgroundColor: '#331B65' }}
                >
                  {uploadingPhoto ? 'Uploading...' : 'Upload Profile Photo'}
                </Button>
              </label>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Username:</strong> {user?.username}
              </Typography>
              {user?.email && (
                <Typography variant="body1" gutterBottom>
                  <strong>Email:</strong> {user.email}
                </Typography>
              )}
              {user?.zipCode && (
                <Typography variant="body1" gutterBottom>
                  <strong>Zip Code:</strong> {user.zipCode}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  <strong>Troop:</strong> {troopName}
                </Typography>
                <IconButton size="small" onClick={openTroopChange} sx={{ ml: 1 }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              <Button size="small" onClick={openTroopChange} sx={{ mb: 1 }}>
                Change troop
              </Button>
              <Typography variant="body1" gutterBottom>
                <strong>Helpful Count:</strong> {user?.helpfulCount || 0}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Lifetime Hearts:</strong> {user?.lifetimeHearts ?? user?.heartsReceived ?? 0}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Button variant="outlined" onClick={handleClickOpen} fullWidth sx={{ mb: 1, borderColor: '#2A1C50', color: '#2A1C50' }}>
              Delete Account
            </Button>
            <Typography variant="caption" color="text.secondary" align="center" display="block">
              Permanent - you&apos;ll lose all your data
            </Typography>

            <Dialog open={open} onClose={handleClose}>
              <DialogTitle>Confirm Account Deletion</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  To delete your account, please type &quot;Close account&quot; to confirm.
                </DialogContentText>
                <TextField
                  autoFocus
                  margin="dense"
                  label='Type "Close account"'
                  type="text"
                  fullWidth
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  error={!!error}
                  helperText={error || 'This action cannot be undone.'}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} color="primary">Cancel</Button>
                <Button
                  onClick={handleDelete}
                  sx={{ color: '#2A1C50' }}
                  disabled={confirmationText !== 'Close account'}
                >
                  Delete Account
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={troopDialogOpen}
              onClose={() => {
                setTroopDialogOpen(false);
                setTroopStep(1);
              }}
              maxWidth="md"
              fullWidth
              fullScreen={troopStep === 2}
              PaperProps={{
                sx: troopStep === 2
                  ? { m: 0, height: '100%', maxHeight: '100%', borderRadius: 0 }
                  : undefined
              }}
            >
              {troopStep === 1 ? (
                <>
                  <DialogTitle>Enter Your Zip Code</DialogTitle>
                  <DialogContent>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        We&apos;ll show the closest active troop zones near your zip.
                      </Typography>
                      <TextField
                        fullWidth
                        required
                        id="zipCode"
                        label="Zip Code"
                        name="zipCode"
                        value={newZipCode}
                        onChange={(e) => setNewZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                        sx={{ mt: 1 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {zipValidating ? (
                                <CircularProgress size={24} />
                              ) : zipValid ? (
                                <IconButton edge="end" onClick={handleZipNext} sx={{ color: 'green' }}>
                                  <CheckCircleIcon />
                                </IconButton>
                              ) : (
                                <IconButton edge="end" disabled sx={{ color: 'grey' }}>
                                  <ArrowForwardIcon />
                                </IconButton>
                              )}
                            </InputAdornment>
                          )
                        }}
                      />
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={() => { setTroopDialogOpen(false); setTroopStep(1); }}
                      sx={{ color: '#331B65' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleZipNext}
                      disabled={!zipValid}
                      sx={{ backgroundColor: '#59458C', '&:hover': { backgroundColor: '#331B65' } }}
                    >
                      Continue
                    </Button>
                  </DialogActions>
                </>
              ) : (
                <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}>
                  {userCenter && (
                    <TroopZonePickerMap
                      userCenter={userCenter}
                      zones={zones}
                      selectedZoneId={selectedZoneId}
                      onSelectZone={setSelectedZoneId}
                      onConfirm={() => setConfirmResetOpen(true)}
                      onBack={() => setTroopStep(1)}
                      confirmLabel="Continue"
                      busy={updatingTroop}
                      fillParent
                    />
                  )}
                </Box>
              )}
            </Dialog>

            <Dialog open={confirmResetOpen} onClose={() => setConfirmResetOpen(false)}>
              <DialogTitle>Switch troops?</DialogTitle>
              <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Switching troops resets your troop-scoped reputation (helpful count, hearts, thanks). This cannot be undone.
                </Alert>
                <DialogContentText>
                  Join the selected troop and reset your reputation?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirmResetOpen(false)}>Cancel</Button>
                <Button
                  sx={{ backgroundColor: '#331B65' }}
                  variant="contained"
                  onClick={handleConfirmTroop}
                  disabled={updatingTroop}
                >
                  {updatingTroop ? 'Switching...' : 'Switch & reset'}
                </Button>
              </DialogActions>
            </Dialog>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Account;

